import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ArticlesProvider } from './hooks/useArticles';
import { ToastProvider } from './hooks/useToast';
import { setTitle, getEnvironmentClasses } from './utils/environment';
import Login from './pages/Login';
import Register from './pages/Register';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Dashboard from './pages/Dashboard';
import Stats from './pages/Stats';
import Topics from './pages/Topics';
import TopicDetail from './pages/TopicDetail';
import AllHighlights from './pages/AllHighlights';
import ArticleViewer from './components/ArticleViewer';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/article/:id"
        element={
          <ProtectedRoute>
            <ArticleViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/topics"
        element={
          <ProtectedRoute>
            <Topics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/topics/:topicId"
        element={
          <ProtectedRoute>
            <TopicDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <Stats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/highlights"
        element={
          <ProtectedRoute>
            <AllHighlights />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 fallback */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="text-6xl font-bold text-gray-300 mb-4">404</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
              <p className="text-gray-600 mb-6">
                The page you&apos;re looking for doesn&apos;t exist.
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Set the title with environment prefix on app load
    setTitle();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ArticlesProvider>
          <ToastProvider>
            <div className={`App ${getEnvironmentClasses()}`}>
              <AppRoutes />
            </div>
          </ToastProvider>
        </ArticlesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;